import type { ComponentProps } from 'react';
import React, { useRef } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { A, Footer as HTMLFooter } from '@expo/html-elements';
import {
  BodyText,
  Container,
  FadeView,
  HStack,
  LoadingDots,
  theme,
  TYPE_CONFIG,
  useBreakpointHelpers,
  useContainerSpacing,
  usePressableState,
  useResponsive,
} from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';
import { getAppVersion } from '@order/utils';

import { useKustomerAvailability } from '../../integrations/kustomer';
import { useContactUsHandler } from '../../integrations/kustomer/kustomer.hooks';
import { useSocialLinks } from '../Account/AccountMenu/hooks';
import { OrgLinks } from './OrgLinksFooter.constants';

export const OrgLinksFooter = () => {
  const { isMobile, isTablet, checkMinWidth } = useBreakpointHelpers();
  const containerSpacing = useContainerSpacing();

  const footerStyles = [
    styles.footer,
    checkMinWidth.largeDisplay && styles.justifyFooter,
    { paddingVertical: containerSpacing },
  ];

  return (
    <HTMLFooter style={footerStyles}>
      <Container>
        {isMobile ? <MobileView /> : null}
        {isTablet ? <TabletView /> : null}
        {checkMinWidth.desktop ? <DesktopView /> : null}
      </Container>
    </HTMLFooter>
  );
};

const DesktopView = () => (
  <HStack itemsPerRow={6}>
    <SupportAndServices />
    <SocialMedia />
    <TermsAndConditions />
    <DownloadTheApp />
    <View />
    <CopyrightAndAppVersion />
  </HStack>
);

const TabletView = () => (
  <HStack style={styles.row} itemsPerRow={4}>
    <View>
      <SupportAndServices />
      <Divider />
      <TermsAndConditions />
    </View>
    <View>
      <SocialMedia />
      <Divider />
      <DownloadTheApp />
    </View>
    <View />
    <CopyrightAndAppVersion />
  </HStack>
);

const MobileView = () => (
  <View>
    <SupportAndServices />
    <Divider />
    <TermsAndConditions />
    <Divider />
    <DownloadTheApp />
    <Divider />
    <SocialMedia />
    <Divider />
    <CopyrightAndAppVersion />
  </View>
);

const SupportAndServices = () => {
  const { t } = useLocalizationContext();

  const contactUsRef = useRef(null);
  const contactUsPressableState = usePressableState(contactUsRef);

  const isKustomerAvailable = useKustomerAvailability();
  const { handleContactUs, handlingContactUs } = useContactUsHandler(
    isKustomerAvailable,
    'org-links',
  );

  return (
    <View>
      <BodyText style={styles.title}>
        {t('footer.support-and-services.title')}
      </BodyText>
      <FooterLink href={OrgLinks.sweetgreen} style={styles.label}>
        {t('footer.support-and-services.sweetgreen')}
      </FooterLink>
      <FooterLink href={OrgLinks.faqs} style={styles.label}>
        {t('footer.support-and-services.faqs')}
      </FooterLink>
      <Pressable
        ref={contactUsRef}
        accessibilityRole="button"
        onPress={handleContactUs}
        style={styles.label}
      >
        <BodyText underline={contactUsPressableState.isHovered}>
          {t('footer.support-and-services.contact-us')}
        </BodyText>
        <FadeView show={handlingContactUs} style={styles.loading}>
          <LoadingDots />
        </FadeView>
      </Pressable>
    </View>
  );
};

const SocialMedia = () => {
  const { t } = useLocalizationContext();
  const socialMedia = useSocialLinks();

  return (
    <View>
      <BodyText style={styles.title}>{t('footer.social-media.title')}</BodyText>
      <FooterLink
        href={OrgLinks.instagram}
        style={styles.label}
        onPress={socialMedia.handleInstagramTelemetry}
      >
        {t('footer.social-media.instagram')}
      </FooterLink>
      <FooterLink
        href={OrgLinks.twitter}
        style={styles.label}
        onPress={socialMedia.handleTwitterTelemetry}
      >
        {t('footer.social-media.twitter')}
      </FooterLink>
      <FooterLink
        href={OrgLinks.tiktok}
        style={styles.label}
        onPress={socialMedia.handleTiktokTelemetry}
      >
        {t('footer.social-media.tiktok')}
      </FooterLink>
      <FooterLink
        href={OrgLinks.spotify}
        style={styles.label}
        onPress={socialMedia.handleSpotifyTelemetry}
      >
        {t('footer.social-media.spotify')}
      </FooterLink>
      <FooterLink
        href={OrgLinks.facebook}
        style={styles.label}
        onPress={socialMedia.handleFacebookTelemetry}
      >
        {t('footer.social-media.facebook')}
      </FooterLink>
    </View>
  );
};

const TermsAndConditions = () => {
  const { t } = useLocalizationContext();

  return (
    <View>
      <BodyText style={styles.title}>
        {t('footer.terms-and-conditions.title')}
      </BodyText>
      <FooterLink href={OrgLinks.privacy} style={styles.label}>
        {t('footer.terms-and-conditions.privacy')}
      </FooterLink>
      <FooterLink href={OrgLinks.terms} style={styles.label}>
        {t('footer.terms-and-conditions.terms')}
      </FooterLink>
      <FooterLink href={OrgLinks.personal} style={styles.label}>
        {t('footer.terms-and-conditions.personal')}
      </FooterLink>
    </View>
  );
};

const DownloadTheApp = () => {
  const { t } = useLocalizationContext();

  return (
    <View>
      <BodyText style={styles.title}>
        {t('footer.download-the-app.title')}
      </BodyText>
      <FooterLink href={OrgLinks.ios} style={styles.label}>
        {t('footer.download-the-app.ios')}
      </FooterLink>
      <FooterLink href={OrgLinks.android} style={styles.label}>
        {t('footer.download-the-app.android')}
      </FooterLink>
    </View>
  );
};

const CopyrightAndAppVersion = () => {
  const { t } = useLocalizationContext();
  const { match } = useResponsive();

  const textStyle = match([undefined, styles.copyrightAndAppVersionText]);

  return (
    <>
      <BodyText style={textStyle}>
        {t('footer.copyright', { year: new Date().getFullYear() })}
      </BodyText>

      <BodyText style={textStyle}>v{getAppVersion()}</BodyText>
    </>
  );
};

const FooterLink = (props: ComponentProps<typeof A>) => {
  const { href, style, children, ...restProps } = props;

  const linkRef = useRef(null);
  const linkPressableState = usePressableState(linkRef);

  const linkStyles = [
    styles.link,
    linkPressableState.isHovered ? styles.linkHover : {},
    style,
  ];

  return (
    <View style={styles.linkWrapper}>
      <A
        ref={linkRef}
        href={href}
        style={linkStyles}
        accessibilityRole="link"
        {...{ hrefAttrs: { target: '_blank' } }}
        {...restProps}
      >
        {children}
      </A>
    </View>
  );
};

const Divider = () => {
  return <View style={styles.divider} />;
};

// ─── STYLES ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  footer: {
    backgroundColor: theme.colors.SKY,
    marginTop: 'auto', // stick footer at the bottom
  },
  justifyFooter: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  row: {
    width: '100%',
    maxWidth: 1840,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  title: {
    marginBottom: theme.spacing.medium,
    textTransform: 'uppercase',
  },
  divider: {
    marginTop: theme.spacing.xlarge,
  },
  label: {
    flexDirection: 'row',
    marginBottom: theme.spacing.xsmall,
  },
  loading: {
    marginLeft: theme.spacing.small,
  },
  linkWrapper: {
    alignItems: 'flex-start',
  },
  link: {
    color: theme.colors.DARK_KALE,
    ...TYPE_CONFIG.BODY['16'],
    fontWeight: 'normal',
  },
  linkHover: {
    textDecorationLine: 'underline',
  },
  copyrightAndAppVersionText: {
    textAlign: 'right',
  },
});
